import { Text } from '@mantine/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../../react-commons/query/QueryBaseComponent';
import { DisplayComponentBaseView } from '../../../../../../../react-commons/views/DisplayComponentBaseView';
import { ResourceType } from '../../../../../resource/enumerations/ResourceType';
import {
    defaultProcessAreaRoleRelationResource,
    ProcessAreaRoleRelationMutationResourceSchema,
    ProcessAreaRoleRelationPropertyValidation,
    useProcessAreaRoleRelationActions,
} from '../../../../../resource/resources/artifacts/role/processAreaRoleRelationResource';
import { Form_DisplayComponentBaseView } from '../../../../reusable-components/form-components/Form_DisplayComponentBaseView';
import { SingleParentedOverviewComponent } from '../../../../reusable-components/resource-overviews/SingleParentedOverviewComponent';
import { ResourceValidationTitleBar } from '../../../../reusable-components/validation/ResourceValidationTitleBar';
import { OverviewComponentPropertyValidationInformation } from '../../../../util/overviewUtils';
import { selectNoScopeValidationScope, selectReadOnly } from '../../../../util/useSelectorFunctions';
import { ProcessAreaRoleRelationContent } from './ProcessAreaRoleRelationContent';

const ProcessAreaRoleRelationOverviewContent = ({
    id,
    displayedInOverview,
}: {
    id: string;
    displayedInOverview: boolean;
}) => {
    const readOnly = useSelector(selectReadOnly);

    const processAreaRoleRelationActions = useProcessAreaRoleRelationActions();
    const processAreaRoleRelationQuery = processAreaRoleRelationActions.useGetResource(id);
    const updateProcessAreaRoleRelation = processAreaRoleRelationActions.useUpdateResource();

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProcessAreaRoleRelationMutationResourceSchema,
        defaultValues: defaultProcessAreaRoleRelationResource,
        submitHandler: async (data) => {
            await updateProcessAreaRoleRelation.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: processAreaRoleRelationQuery.data,
    });

    return (
        <QueryBaseComponent name="ProcessAreaRoleRelation" query={processAreaRoleRelationQuery}>
            <Form_DisplayComponentBaseView onSubmit={onSubmit} form={form} canSubmitDefaultValue={false}>
                <ProcessAreaRoleRelationContent
                    displayedInOverview={displayedInOverview}
                    form={form}
                    formNames={formNames}
                    readOnly={readOnly}
                    id={id}
                />
            </Form_DisplayComponentBaseView>
        </QueryBaseComponent>
    );
};

export const ProcessAreaRoleRelationOverviewComponent = ({ parentId }: { parentId: string }) => {
    const validationScope = useSelector(selectNoScopeValidationScope);

    const processAreaRoleRelationActions = useProcessAreaRoleRelationActions();

    const parametersQuery = processAreaRoleRelationActions.useGetAllResources(parentId);

    const resourceType = ResourceType.PROCESS_AREA_ROLE_RELATION;

    return (
        <QueryBaseComponent name="ProcessAreaRoleRelations" query={parametersQuery}>
            <SingleParentedOverviewComponent
                label={"Role Relations"}
                resourceQuery={parametersQuery}
                content={(id, displayedInOverview) => (
                    <ProcessAreaRoleRelationOverviewContent id={id} displayedInOverview={displayedInOverview} />
                )}
                propertyValidationInformation={{
                    namePropertyIdentifier: ProcessAreaRoleRelationPropertyValidation.NAME,
                    resourceType,
                }}
                validationScope={validationScope}
                parentId={parentId}
            />
        </QueryBaseComponent>
    );
};
